import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useIdentity } from "@/auth/identity";
import { toast } from "@/hooks/use-toast";
import { CheckCircle2, ArrowLeft, AlertTriangle, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { tripService } from "@/services/api";
import { useDriver } from "@/context/DriverContext";
import BottomNavigation from "@/components/BottomNavigation";

const EndTrip = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useIdentity();
  const { todayShipments, updateShipmentStatus } = useDriver();

  // El id viene de la tarjeta que lanzó la navegación. Si se perdió (refresh)
  // y hay una sola ruta hoy, se usa esa; con dos, no hay forma de adivinar.
  const stateId = (location.state as { shipmentId?: string } | null)?.shipmentId;
  const shipmentId =
    stateId ?? (todayShipments.length === 1 ? todayShipments[0].id : null);

  useEffect(() => {
    if (!shipmentId) {
      toast({
        title: "Elegí la ruta",
        description: "Volvé al inicio y tocá la ruta que querés finalizar.",
        variant: "destructive",
      });
      navigate("/");
    }
  }, [shipmentId, navigate]);

  const [formData, setFormData] = useState({
    tripStatus: "success" as "success" | "problems",
    issueDescription: "",
    missingPackages: 0,
    failedPackages: 0,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!shipmentId) {
      toast({
        title: "Error",
        description: "No hay un viaje activo para finalizar",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let shipmentData;

      if (formData.tripStatus === "problems") {
        // Si tuvo problemas, enviar como PROBLEMAS con comentario
        shipmentData = {
          routeCompleted: true,
          status: "PROBLEMAS",
          comment: formData.issueDescription,
          missingPackages: formData.missingPackages || 0,
          failedPackages: formData.failedPackages || 0,
        };
      } else {
        // Si no tuvo problemas, enviar como completado
        shipmentData = {
          routeCompleted: true,
          status: "COMPLETADO",
          missingPackages: formData.missingPackages || 0,
          failedPackages: formData.failedPackages || 0,
        };
      }

      await tripService.endShipment(shipmentId, shipmentData);

      // Actualizar el estado de ESA ruta en el contexto
      updateShipmentStatus(
        shipmentId,
        formData.tripStatus === "problems" ? "PROBLEMAS" : "COMPLETADO"
      );

      toast({
        title: "Éxito",
        description: "Viaje finalizado correctamente",
      });

      navigate("/");
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "No se pudo finalizar el viaje",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      issueDescription: e.target.value,
    });
  };

  const handleTripStatusChange = (value: string) => {
    setFormData({
      ...formData,
      tripStatus: value as "success" | "problems",
      issueDescription: value === "problems" ? formData.issueDescription : "",
    });
  };

  const handleNumberInputChange = (field: "missingPackages" | "failedPackages", value: string) => {
    const numValue = value === "" ? 0 : parseInt(value, 10);
    if (isNaN(numValue) || numValue < 0) {
      return; // No actualizar si el valor no es válido
    }
    setFormData({
      ...formData,
      [field]: numValue,
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-16">
      {/* Header */}
      <header className="bg-primary text-white p-4 flex items-center">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="text-white mr-2"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-xl font-bold">Finalizar Viaje</h1>
        </div>
      </header>

      {/* Form */}
      <main className="container mx-auto p-4 max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Estado del Viaje */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Estado del Viaje</CardTitle>
              <CardDescription>
                Seleccione cómo finalizó el viaje
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <RadioGroup
                value={formData.tripStatus}
                onValueChange={handleTripStatusChange}
                className="space-y-3"
              >
                {/* Opción: Viaje Exitoso (Default) */}
                <Label
                  htmlFor="success"
                  className={`relative flex items-start space-x-3 rounded-lg border-2 p-4 transition-all cursor-pointer ${formData.tripStatus === "success"
                    ? "border-green-500 bg-green-50"
                    : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                >
                  <RadioGroupItem
                    value="success"
                    id="success"
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <CheckCircle2
                        className={`h-5 w-5 ${formData.tripStatus === "success"
                          ? "text-green-600"
                          : "text-gray-400"
                          }`}
                      />
                      <span
                        className={`text-base font-semibold ${formData.tripStatus === "success"
                          ? "text-green-900"
                          : "text-gray-700"
                          }`}
                      >
                        Viaje completado exitosamente
                      </span>
                    </div>
                    <p
                      className={`text-sm mt-1 ${formData.tripStatus === "success"
                        ? "text-green-700"
                        : "text-gray-500"
                        }`}
                    >
                      El viaje se completó sin inconvenientes. Esta es la opción por defecto.
                    </p>
                  </div>
                </Label>

                {/* Opción: Tuve Problemas */}
                <Label
                  htmlFor="problems"
                  className={`relative flex items-start space-x-3 rounded-lg border-2 p-4 transition-all cursor-pointer ${formData.tripStatus === "problems"
                    ? "border-red-500 bg-red-50"
                    : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                >
                  <RadioGroupItem
                    value="problems"
                    id="problems"
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle
                        className={`h-5 w-5 ${formData.tripStatus === "problems"
                          ? "text-red-600"
                          : "text-gray-400"
                          }`}
                      />
                      <span
                        className={`text-base font-semibold ${formData.tripStatus === "problems"
                          ? "text-red-900"
                          : "text-gray-700"
                          }`}
                      >
                        Tuve problemas durante el viaje
                      </span>
                    </div>
                    <p
                      className={`text-sm mt-1 ${formData.tripStatus === "problems"
                        ? "text-red-700"
                        : "text-gray-500"
                        }`}
                    >
                      Seleccione esta opción si encontró algún problema o inconveniente durante el viaje.
                    </p>
                  </div>
                </Label>
              </RadioGroup>

              {/* Descripción de problemas (solo si se seleccionó problemas) */}
              {formData.tripStatus === "problems" && (
                <div className="mt-4 pt-4 border-t">
                  <Label
                    htmlFor="issueDescription"
                    className="block mb-2 font-medium text-gray-700"
                  >
                    Describa los problemas encontrados
                  </Label>
                  <Textarea
                    id="issueDescription"
                    placeholder="Describa detalladamente los problemas o inconvenientes que encontró durante el viaje..."
                    value={formData.issueDescription}
                    onChange={handleTextareaChange}
                    className="w-full"
                    rows={4}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Esta información será registrada junto con el estado del viaje.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Información de Paquetes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center space-x-2">
                <Package className="h-5 w-5" />
                <span>Información de Paquetes</span>
              </CardTitle>
              <CardDescription>
                Reporte la cantidad de paquetes faltantes o no entregados
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor="missingPackages"
                    className="block mb-2 font-medium text-gray-700"
                  >
                    Paquetes Faltantes
                  </Label>
                  <Input
                    id="missingPackages"
                    type="number"
                    min="0"
                    placeholder="0"
                    value={formData.missingPackages}
                    onChange={(e) =>
                      handleNumberInputChange("missingPackages", e.target.value)
                    }
                    className="w-full"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="failedPackages"
                    className="block mb-2 font-medium text-gray-700"
                  >
                    Paquetes No Entregados
                  </Label>
                  <Input
                    id="failedPackages"
                    type="number"
                    min="0"
                    placeholder="0"
                    value={formData.failedPackages}
                    onChange={(e) =>
                      handleNumberInputChange("failedPackages", e.target.value)
                    }
                    className="w-full"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Button
            type="submit"
            className={`w-full py-6 ${formData.tripStatus === "success"
              ? "bg-green-600 hover:bg-green-700 text-white"
              : "bg-red-600 hover:bg-red-700 text-white"
              }`}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Procesando..." : "Finalizar Viaje"}
          </Button>
        </form>
      </main>

      <BottomNavigation />
    </div>
  );
};

export default EndTrip;
